import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BrandAssetInput,
  BrandIdentityInput,
  ClientFeatureInput,
  ClientGoalsInput,
  ClientPlanInput,
  ClientProfileInput,
  CreateClientInput,
  Paginated,
  ServiceAreaInput,
  UpdateClientInput,
} from '@marketing-os/shared';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut , apiGetList } from '@/lib/api';
import { qk, queryClient } from '@/lib/queryClient';
import type {
  BrandAsset,
  BrandIdentity,
  Client,
  ClientFeature,
  ClientGoals,
  ClientPlan,
  ClientProfile,
  KnowledgeBaseResponse,
  ServiceArea,
} from '@/types/api';

export interface ClientDetail extends Client {
  profile: ClientProfile | null;
  goals: ClientGoals | null;
  brandIdentity: BrandIdentity | null;
}

export interface ClientListParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

export function useClients(params: ClientListParams = {}) {
  return useQuery({
    queryKey: qk.clients(params),
    queryFn: () => apiGet<Paginated<Client>>('/clients', { params }),
  });
}

/** Lista enxuta para seletores (switcher, filtros). */
export function useClientOptions() {
  return useQuery({
    queryKey: qk.clients({ scope: 'options' }),
    queryFn: () => apiGet<Paginated<Client>>('/clients', { params: { page: 1, pageSize: 200 } }),
    staleTime: 5 * 60_000,
    select: (data) => data.items,
  });
}

export function useClient(clientId: string | undefined) {
  return useQuery({
    queryKey: qk.client(clientId ?? ''),
    queryFn: () => apiGet<ClientDetail>(`/clients/${clientId}`),
    enabled: Boolean(clientId),
  });
}

export function useKnowledgeBase(clientId: string | undefined) {
  return useQuery({
    queryKey: qk.knowledgeBase(clientId ?? ''),
    queryFn: () => apiGet<KnowledgeBaseResponse>(`/clients/${clientId}/knowledge-base`),
    enabled: Boolean(clientId),
  });
}

export function useCreateClient() {
  return useMutation({
    mutationFn: (input: CreateClientInput) => apiPost<Client, CreateClientInput>('/clients', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useUpdateClient(clientId: string) {
  return useMutation({
    mutationFn: (input: UpdateClientInput) =>
      apiPatch<Client, UpdateClientInput>(`/clients/${clientId}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useArchiveClient() {
  return useMutation({
    mutationFn: (clientId: string) => apiDelete(`/clients/${clientId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useUpsertProfile(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ClientProfileInput) =>
      apiPut<ClientProfile, ClientProfileInput>(`/clients/${clientId}/profile`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.client(clientId) });
      void client.invalidateQueries({ queryKey: qk.knowledgeBase(clientId) });
    },
  });
}

export function useUpsertGoals(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ClientGoalsInput) =>
      apiPut<ClientGoals, ClientGoalsInput>(`/clients/${clientId}/goals`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.client(clientId) }),
  });
}

/* --------------------------------- planos --------------------------------- */

export function usePlans(clientId: string | undefined) {
  return useQuery({
    queryKey: qk.plans(clientId ?? ''),
    queryFn: () => apiGetList<ClientPlan>(`/clients/${clientId}/plans`),
    enabled: Boolean(clientId),
  });
}

export function useSavePlan(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, input }: { planId?: string; input: ClientPlanInput }) =>
      planId
        ? apiPatch<ClientPlan, ClientPlanInput>(`/clients/${clientId}/plans/${planId}`, input)
        : apiPost<ClientPlan, ClientPlanInput>(`/clients/${clientId}/plans`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.plans(clientId) }),
  });
}

export function useDeletePlan(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => apiDelete(`/clients/${clientId}/plans/${planId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.plans(clientId) }),
  });
}

/* --------------------------------- regiões -------------------------------- */

export function useServiceAreas(clientId: string | undefined) {
  return useQuery({
    queryKey: qk.serviceAreas(clientId ?? ''),
    queryFn: () => apiGetList<ServiceArea>(`/clients/${clientId}/service-areas`),
    enabled: Boolean(clientId),
  });
}

export function useSaveServiceArea(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ areaId, input }: { areaId?: string; input: ServiceAreaInput }) =>
      areaId
        ? apiPatch<ServiceArea, ServiceAreaInput>(`/clients/${clientId}/service-areas/${areaId}`, input)
        : apiPost<ServiceArea, ServiceAreaInput>(`/clients/${clientId}/service-areas`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.serviceAreas(clientId) }),
  });
}

export function useDeleteServiceArea(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (areaId: string) => apiDelete(`/clients/${clientId}/service-areas/${areaId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.serviceAreas(clientId) }),
  });
}

/* ----------------------------- características ---------------------------- */

export function useFeatures(clientId: string | undefined) {
  return useQuery({
    queryKey: qk.features(clientId ?? ''),
    queryFn: () => apiGetList<ClientFeature>(`/clients/${clientId}/features`),
    enabled: Boolean(clientId),
  });
}

export function useUpsertFeature(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ClientFeatureInput) =>
      apiPut<ClientFeature, ClientFeatureInput>(`/clients/${clientId}/features`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.features(clientId) }),
  });
}

export function useDeleteFeature(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (featureId: string) => apiDelete(`/clients/${clientId}/features/${featureId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.features(clientId) }),
  });
}

/* --------------------------- identidade visual ---------------------------- */

export function useBrandIdentity(clientId: string | undefined) {
  return useQuery({
    queryKey: qk.brandIdentity(clientId ?? ''),
    queryFn: () => apiGet<BrandIdentity | null>(`/clients/${clientId}/brand/identity`),
    enabled: Boolean(clientId),
  });
}

export function useSaveBrandIdentity(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: BrandIdentityInput) =>
      apiPut<BrandIdentity, BrandIdentityInput>(`/clients/${clientId}/brand/identity`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.brandIdentity(clientId) }),
  });
}

export function useBrandAssets(clientId: string | undefined) {
  return useQuery({
    queryKey: qk.brandAssets(clientId ?? ''),
    queryFn: () => apiGetList<BrandAsset>(`/clients/${clientId}/brand/assets`),
    enabled: Boolean(clientId),
  });
}

export function useCreateBrandAsset(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: BrandAssetInput) =>
      apiPost<BrandAsset, BrandAssetInput>(`/clients/${clientId}/brand/assets`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.brandAssets(clientId) }),
  });
}

export function useDeleteBrandAsset(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => apiDelete(`/clients/${clientId}/brand/assets/${assetId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.brandAssets(clientId) }),
  });
}
